import React, { useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    Divider,
    Paper,
    Stack,
    Grid,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Slider,
    ToggleButton,
    ToggleButtonGroup,
    Button,
} from '@mui/material';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import ImageIcon from '@mui/icons-material/Image';
import { Breakpoint, DesignElement, ElementStyle, TextAlign } from '../../../types/coupon';
import { ColorPicker } from './ColorPicker';
import AssetSelectionModal from './AssetSelectionModal';

// Get base URL for images from environment or default
const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

interface ElementPropertiesProps {
    element: DesignElement;
    onUpdate: (element: DesignElement) => void;
    currentBreakpoint: Breakpoint;
}

type StyleProperty = keyof ElementStyle;

export const ElementProperties: React.FC<ElementPropertiesProps> = ({
    element,
    onUpdate,
    currentBreakpoint,
}) => {
    const [assetModalOpen, setAssetModalOpen] = useState(false);

    const handlePositionChange = (property: keyof typeof element.responsive[typeof currentBreakpoint], value: number) => {
        onUpdate({
            ...element,
            responsive: {
                ...element.responsive,
                [currentBreakpoint]: {
                    ...element.responsive[currentBreakpoint],
                    [property]: value
                }
            }
        });
    };

    const handleStyleChange = (property: StyleProperty, value: string | number) => {
        onUpdate({
            ...element,
            style: {
                ...element.style,
                [property]: value
            }
        });
    };

    const handleContentChange = (value: string) => {
        onUpdate({
            ...element,
            content: value
        });
    };

    const handleRotationChange = (event: Event, newValue: number | number[]) => {
        if (typeof newValue !== 'number') return;
        
        onUpdate({
            ...element,
            responsive: {
                ...element.responsive,
                [currentBreakpoint]: {
                    ...element.responsive[currentBreakpoint],
                    rotation: newValue
                }
            }
        });
    };

    const getImageUrl = (path: string) => {
        const cleanPath = path.replace(/^\/+/, '');
        const url = `${BASE_URL}/${cleanPath}`;
        return process.env.NODE_ENV === 'development' ? `${url}?t=${new Date().getTime()}` : url;
    };

    return (
        <Box>
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    mb: 2, 
                    bgcolor: '#f5f5f5',
                    border: '1px solid',
                    borderColor: '#e0e0e0'
                }}
            >
                <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ 
                        color: '#1976d2',
                        fontWeight: 600
                    }}
                >
                    Element Name
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    value={element.name || ''}
                    onChange={(e) => onUpdate({
                        ...element,
                        name: e.target.value
                    })}
                    placeholder={`Unnamed ${element.type.charAt(0).toUpperCase() + element.type.slice(1)}`}
                />
            </Paper>

            {element.type === 'image' && (
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 2, 
                        mb: 2, 
                        bgcolor: '#f5f5f5',
                        border: '1px solid',
                        borderColor: '#e0e0e0'
                    }}
                >
                    <Typography 
                        variant="subtitle2" 
                        gutterBottom
                        sx={{ 
                            color: '#1976d2',
                            fontWeight: 600
                        }}
                    >
                        Image
                    </Typography>
                    <Stack spacing={2}>
                        {element.content ? (
                            <Box>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 140,
                                        backgroundColor: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <img
                                        src={getImageUrl(element.content)}
                                        alt={element.alt || ''}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </Box>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => setAssetModalOpen(true)}
                                >
                                    Change Image
                                </Button>
                            </Box>
                        ) : (
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<ImageIcon />}
                                onClick={() => setAssetModalOpen(true)}
                            >
                                Choose Image
                            </Button>
                        )}
                        <TextField
                            label="Alt Text"
                            value={element.alt || ''}
                            onChange={(e) => onUpdate({
                                ...element,
                                alt: e.target.value
                            })}
                            size="small"
                            fullWidth
                            helperText="Describe the image for accessibility"
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel>Object Fit</InputLabel>
                            <Select
                                value={element.style?.objectFit || 'contain'}
                                onChange={(e) => handleStyleChange('objectFit', e.target.value)}
                                label="Object Fit"
                            >
                                <MenuItem value="contain">Contain</MenuItem>
                                <MenuItem value="cover">Cover</MenuItem>
                                <MenuItem value="fill">Fill</MenuItem>
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="scale-down">Scale Down</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Paper>
            )}

            {(element.type === 'text' || element.type === 'dynamic') && (
                <>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 2, 
                            mb: 2, 
                            bgcolor: '#f5f5f5',
                            border: '1px solid',
                            borderColor: '#e0e0e0'
                        }}
                    >
                        <Typography 
                            variant="subtitle2" 
                            gutterBottom
                            sx={{ 
                                color: '#1976d2',
                                fontWeight: 600
                            }}
                        >
                            {element.type === 'text' ? 'Text Content' : 'Dynamic Field'}
                        </Typography>
                        {element.type === 'text' ? (
                            <TextField
                                fullWidth
                                placeholder="Enter text..."
                                value={element.content || ''}
                                onChange={(e) => handleContentChange(e.target.value)}
                                multiline
                                rows={2}
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: '#ffffff'
                                    }
                                }}
                            />
                        ) : (
                            <>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Field Type</InputLabel>
                                    <Select
                                        value={element.dynamicField || 'email'}
                                        onChange={(e) => {
                                            const fieldType = e.target.value as 'email' | 'fullName' | 'todays_date';
                                            onUpdate({
                                                ...element,
                                                dynamicField: fieldType,
                                                content: `{${fieldType}}`
                                            });
                                        }}
                                        label="Field Type"
                                    >
                                        <MenuItem value="email">Email Address</MenuItem>
                                        <MenuItem value="fullName">Full Name</MenuItem>
                                        <MenuItem value="todays_date">Today's Date</MenuItem>
                                    </Select>
                                </FormControl>
                                <Typography 
                                    variant="caption" 
                                    color="textSecondary" 
                                    sx={{ display: 'block', mt: 1 }}
                                >
                                    This field will be automatically populated with the customer's {element.dynamicField || 'email'}.
                                </Typography>
                            </>
                        )}
                    </Paper>

                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 2, 
                            mb: 2, 
                            bgcolor: '#f5f5f5',
                            border: '1px solid',
                            borderColor: '#e0e0e0'
                        }}
                    >
                        <Typography 
                            variant="subtitle2" 
                            gutterBottom
                            sx={{ 
                                color: '#1976d2',
                                fontWeight: 600
                            }}
                        >
                            Text Style
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="Font Size"
                                type="number"
                                value={element.style?.fontSize ?? ''}
                                onChange={(e) => {
                                    const size = Number(e.target.value);
                                    if (!isNaN(size)) {
                                        handleStyleChange('fontSize', size);
                                    }
                                }}
                                size="small"
                                fullWidth
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">px</InputAdornment>
                                }}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel>Font Family</InputLabel>
                                <Select
                                    value={element.style?.fontFamily || 'Arial'}
                                    onChange={(e) => handleStyleChange('fontFamily', e.target.value)}
                                    label="Font Family"
                                >
                                    <MenuItem value="Arial">Arial</MenuItem>
                                    <MenuItem value="Helvetica">Helvetica</MenuItem>
                                    <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                                    <MenuItem value="Georgia">Georgia</MenuItem>
                                    <MenuItem value="Verdana">Verdana</MenuItem>
                                    <MenuItem value="Roboto">Roboto</MenuItem>
                                    <MenuItem value="Open Sans">Open Sans</MenuItem>
                                    <MenuItem value="Lato">Lato</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Font Weight</InputLabel>
                                <Select
                                    value={element.style?.fontWeight || 'normal'}
                                    onChange={(e) => handleStyleChange('fontWeight', e.target.value)}
                                    label="Font Weight"
                                >
                                    <MenuItem value="normal">Normal (400)</MenuItem>
                                    <MenuItem value="100">100</MenuItem>
                                    <MenuItem value="200">200</MenuItem>
                                    <MenuItem value="300">300</MenuItem>
                                    <MenuItem value="400">400</MenuItem>
                                    <MenuItem value="500">500</MenuItem>
                                    <MenuItem value="600">600</MenuItem>
                                    <MenuItem value="700">700 (Bold)</MenuItem>
                                    <MenuItem value="800">800</MenuItem>
                                    <MenuItem value="900">900</MenuItem>
                                </Select>
                            </FormControl>
                            <Box>
                                <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                                    Text Alignment
                                </Typography>
                                <ToggleButtonGroup
                                    value={element.style?.textAlign || 'left'}
                                    exclusive
                                    onChange={(e, value) => {
                                        if (value) handleStyleChange('textAlign', value);
                                    }}
                                    size="small"
                                >
                                    <ToggleButton value="left">
                                        <FormatAlignLeftIcon />
                                    </ToggleButton>
                                    <ToggleButton value="center">
                                        <FormatAlignCenterIcon />
                                    </ToggleButton>
                                    <ToggleButton value="right">
                                        <FormatAlignRightIcon />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                                    Text Color
                                </Typography>
                                <ColorPicker
                                    value={element.style?.color || '#000000'}
                                    onChange={(color) => handleStyleChange('color', color)}
                                />
                            </Box>
                        </Stack>
                    </Paper>
                </>
            )}

            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    mb: 2, 
                    bgcolor: '#f5f5f5',
                    border: '1px solid',
                    borderColor: '#e0e0e0'
                }}
            >
                <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ 
                        color: '#1976d2',
                        fontWeight: 600
                    }}
                >
                    Position & Size
                </Typography>
                <Box sx={{ display: 'grid', gap: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="X"
                                type="number"
                                value={element.responsive[currentBreakpoint].x}
                                onChange={(e) => handlePositionChange('x', Number(e.target.value))}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Y"
                                type="number"
                                value={element.responsive[currentBreakpoint].y}
                                onChange={(e) => handlePositionChange('y', Number(e.target.value))}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Width"
                                type="number"
                                value={element.responsive[currentBreakpoint].width}
                                onChange={(e) => handlePositionChange('width', Number(e.target.value))}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Height"
                                type="number"
                                value={element.responsive[currentBreakpoint].height}
                                onChange={(e) => handlePositionChange('height', Number(e.target.value))}
                            />
                        </Grid>
                    </Grid>
                    
                    {/* Rotation control */}
                    <Box>
                        <Typography variant="caption" color="textSecondary" gutterBottom>
                            Rotation
                        </Typography>
                        <Slider
                            value={element.responsive[currentBreakpoint].rotation || 0}
                            onChange={handleRotationChange}
                            min={0}
                            max={360}
                            step={1}
                            marks={[
                                { value: 0, label: '0°' },
                                { value: 90, label: '90°' },
                                { value: 180, label: '180°' },
                                { value: 270, label: '270°' },
                                { value: 360, label: '360°' }
                            ]}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${value}°`}
                        />
                    </Box>
                </Box>
            </Paper>

            <Paper sx={{ p: 2 }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 2,
                        color: 'text.primary',
                        fontWeight: 600
                    }}
                >
                    Style
                </Typography>
                <Stack spacing={2}>
                    <ColorPicker
                        label="Background Color"
                        value={element.style?.backgroundColor || ''}
                        onChange={(color) => handleStyleChange('backgroundColor', color)}
                    />
                    <TextField
                        label="Border"
                        value={element.style?.border || ''}
                        onChange={(e) => handleStyleChange('border', e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="1px solid #000000"
                        helperText="e.g. '1px solid #000000' or '2px dashed red'"
                    />
                    <TextField
                        label="Border Radius"
                        type="number"
                        value={element.style?.borderRadius || ''}
                        onChange={(e) => handleStyleChange('borderRadius', e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                            endAdornment: <InputAdornment position="end">px</InputAdornment>
                        }}
                    />
                </Stack>
            </Paper>
            <AssetSelectionModal
                open={assetModalOpen}
                onClose={() => setAssetModalOpen(false)}
                onSelect={(asset) => {
                    const updatedElement = {
                        ...element,
                        content: asset.url.replace(/^\/+/, ''), // Remove leading slashes
                        alt: asset.originalName,
                        style: {
                            ...element.style,
                            objectFit: element.style?.objectFit || 'contain'
                        }
                    };
                    onUpdate(updatedElement);
                    setAssetModalOpen(false);
                }}
                currentAssetId={element.content}
            />
        </Box>
    );
};
